import { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import http from "../../Common/http-common.js";
import * as common from "../../Common/common.js";
import * as Comp from "../../Common/CommonComponents.js";
import { GetPageAuthorityFn } from "../../Common/authority.js";
import CssClass from "../../Styles/common.module.css";

import ConfirmModal from "../../Components/BasicElements/ConfirmModal.js";
import ExpensesAttachmentModal from "../../Components/BasicElements/ExpensesAttachmentModal.js";
import ExpensesDetailsUpdate from "../../Components/History/Expenses/ExpensesDetailsUpdate";
import ExpensesDetailsDelete from "../../Components/History/Expenses/ExpensesDetailsDelete";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faSquarePlus,faFileImport,faPenToSquare,faTrashCan} from "@fortawesome/free-solid-svg-icons";

import "primeicons/primeicons.css";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";

const PAGE_NAME = "Expenses.js_";
const MODULE_ID = "WEB_AP_003";

const Expenses = (props) => {

    const navigate = useNavigate();
    const [cookies] = useCookies([]);

    const [userId] = useState(cookies.USER_ID);
    const [userName] = useState(cookies.USERNAME);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    const [updateData, setUpdateData] = useState(null);
    const [expensesId, setExpensesId] = useState("");

    const [categoryTypeList, setCategoryTypeList] = useState([]);

    const [editData, setEditData] = useState({
        DATE: "",
        CATEGORY_TYPE_MST_ID: "",
        CATEGORY_TYPE_NAME: "",
        AMOUNT: "",
        REQUIRED_RECEIPT: "",
        EXPENSES_DESC: "",
        REMARK: "",
        IS_MEDICAL: false,
        IS_MILEAGE: false,
        CALCULATED_AMOUNT: ""
    });

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("Loading...");
    const [allData, setAllData] = useState([]);
    const [inputError, setInputError] = useState([]);

    const [actionAuthority, setActionAuthority] = useState({
        READ: "N",
        WRITE: "N"
    });

    const totalAmount = allData?.reduce((sum, row) => {
        const val = parseFloat(row.AMOUNT);
        return sum + (isNaN(val) ? 0 : val);
    }, 0);

    // ATTACHMENT
    const [attachments, setAttachments] = useState([]);
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);
    const [attachmentList, setAttachmentList] = useState([]); 
    const [attachmentSource, setAttachmentSource] = useState("");
    const openAttachmentModal = (id) => {
        setAttachmentSource(id);
        setShowAttachmentModal(true);
    };
    const [attachmentMap, setAttachmentMap] = useState({});

    //MEDICAL CLAIM
    const [medicalInfo, setMedicalInfo] = useState({
        entitled: 0,
        claimed: 0,
        balance: 0
    });

  	// AUTHORITY
    useEffect(() => {

        let promise = GetPageAuthorityFn(userId, MODULE_ID);

        promise.then(
            (result) => {

                setActionAuthority(result);

                if (result.READ === "N") {
                    navigate("/Dashboard");
                }
            },
            (error) => {
                toast.error(error);
                navigate("/Dashboard");
            }
        );

    }, [userId, navigate]);

    // LOAD TYPE
    useEffect(() => {
        GetExpensesType();
    }, []);

    useEffect(() => {
        const loadAll = async () => {
            for (let row of allData) {
                if (row.EXPENSES_DETAILS_ID && attachmentMap[row.EXPENSES_DETAILS_ID] === undefined) {
                    await loadAttachments(row.EXPENSES_DETAILS_ID);
                }
            }
        };

        loadAll();
    }, [allData]);

    //LOAD MEDICAL CLAIM
    useEffect(() => {
    const year = new Date().getFullYear();

        http.get(
            "api/home/GetUserLeaveEntitlement?_sysUser=" +
            userId +
            "&_year=" + year
        )
        .then(res => {
            if (res.data?.length > 0) {
                const data = res.data[0];

                setMedicalInfo({
                    entitled: data.MEDICAL_CLAIM_ENTITLED || 0,
                    claimed: data.MEDICAL_CLAIMED || 0,
                    balance: data.MEDICAL_CLAIM_BALANCE || 0
                });
            }
        })
        .catch((err) => {
            toast.error("Failed to load Medical Claim.");
            common.c_LogWebError(PAGE_NAME, "LoadMedicalClaim", err);
        });
    }, [userId]);

    const GetExpensesType = () => {

        let functionName = "GetExpensesType";

        setLoading(true);
        setLoadingText("Getting Expenses Type...");

        http.get("api/expenses/GetExpensesType?_sysUser=" + userId)

            .then((response) => {

                let arr = [];

                for (let i = 0; i < response.data.length; i++) {

									arr.push({
											key: response.data[i].CATEGORY_TYPE_MST_ID,
											value: response.data[i].CATEGORY_TYPE_MST_ID,
											label: response.data[i].CATEGORY_TYPE_NAME,
											remark: response.data[i].REMARK,
											receiptFlag: response.data[i].RECEIPT_FLAG,
											mileageRate: response.data[i].MILEAGE_RATE ?? 0.5,
									});
                }

                setCategoryTypeList(arr);
            })

            .catch((err) => {
                toast.error("Failed to get expenses type.");
                common.c_LogWebError(PAGE_NAME, functionName, err);
            })

            .finally(() => {
                setLoading(false);
                setLoadingText("Loading...");
            });
    };

    // INPUT
    const inputHandler = (event) => {

        const id = event.target.id;
        const val = event.target.value;

        setEditData((prev) => ({
            ...prev,
            [id]: val
        }));

        if (val.length === 0 || val.trim().length === 0) {
            setInputError((prev) => ({
                ...prev,
                [id]: id + " could not be empty."
            }));
        } else {
            setInputError((prev) => ({
                ...prev,
                [id]: ""
            }));
        }
    };

    const selectTypeHandler = (val) => {

        const selected = categoryTypeList.find(
            (x) => x.value === val.value
        );

        const receiptFlag =
            selected?.receiptFlag?.toString().trim().toUpperCase() || "N";

        const isMedical =
            selected?.label?.toLowerCase().includes("medical");

        const isMileage =
            selected?.label?.toLowerCase().includes("mileage");

        setEditData((prev) => ({
            ...prev,
            CATEGORY_TYPE_MST_ID: val.value,
            CATEGORY_TYPE_NAME: selected?.label || "",
            REQUIRED_RECEIPT: receiptFlag === "Y" ? "YES" : "NO",
            REMARK: selected?.remark === "-" ? "" : selected?.remark,

            IS_MEDICAL: isMedical,
            IS_MILEAGE: isMileage,
            MILEAGE_RATE: isMileage ? (selected?.mileageRate ?? 0.5) : 0,
        }));

        setInputError((prev) => ({
            ...prev,
            CATEGORY_TYPE_MST_ID: ""
        }));
    };

    // ADD
    const AddBtnOnClick = () => {

			let functionName = "AddExpenses";

			setLoading(true);
			setLoadingText("Adding Expenses...");

			const totalClaim =
                totalAmount +
                (
                    editData.IS_MILEAGE
                        ? parseFloat(editData.CALCULATED_AMOUNT || 0)
                        : parseFloat(editData.AMOUNT || 0)
                );

			const data = {
                EXPENSES_MST_ID: expensesId,
                USER_MST_GUID: userId,
                TOTAL_CLAIM: totalClaim,
                UPDATE_ID: userId,
                UPDATE_NAME: userName,

                DATE: editData.DATE ? editData.DATE + "T00:00:00" : null,

                CATEGORY_TYPE_MST_ID: editData.CATEGORY_TYPE_MST_ID,
                CATEGORY_TYPE_NAME: editData.CATEGORY_TYPE_NAME,
                AMOUNT: editData.IS_MILEAGE
                ? editData.CALCULATED_AMOUNT
                : editData.AMOUNT,
                MILEAGE: editData.IS_MILEAGE ? parseFloat(editData.AMOUNT || 0) : 0,
                EXPENSES_DESC: editData.EXPENSES_DESC,
                REMARK: editData.REMARK,

                FROM_SOURCE: {
                    SOURCE: "WEB",
                    MODULE_ID
                }
            };

			http.post("api/expenses/AddExpenses", data)

			.then(async (response) => {

                const mstId = response.data.EXPENSES_MST_ID;
                const detailsId = response.data.EXPENSES_DETAILS_ID;

                setExpensesId(mstId);

                // Upload all attachments first
                for (let f of attachments) {
                    const formData = new FormData();
                    formData.append("file", f.file);
                    formData.append("EXPENSES_DETAILS_ID", detailsId);
                    formData.append("USER_ID", userId);

                    await http.post(
                        "api/expenses/UploadExpensesAttachment",
                        formData,
                        { headers: {"Content-Type": "multipart/form-data"} }
                    );
                }

                // Confirm count from server — sets attachmentMap[detailsId] before row appears
                await loadAttachments(detailsId);

                // Add row only after count is set; allData useEffect sees count != undefined and skips re-fetch
                setAllData((prev) => [
                    ...prev,
                    {
                        ...editData,
                        DATE: editData.DATE + "T00:00:00",
                        EXPENSES_DETAILS_ID: detailsId,
                        EXPENSES_MST_ID: mstId,
                        AMOUNT: editData.IS_MILEAGE
                            ? editData.CALCULATED_AMOUNT
                            : editData.AMOUNT,
                        MILEAGE: editData.IS_MILEAGE ? editData.AMOUNT : 0
                    }
                ]);

                // CLEAR FORM
                setEditData({
                    DATE: "",
                    CATEGORY_TYPE_MST_ID: "",
                    CATEGORY_TYPE_NAME: "",
                    AMOUNT: "",
                    REQUIRED_RECEIPT: "",
                    EXPENSES_DESC: "",
                    REMARK: "",
                    IS_MEDICAL: false,
                    IS_MILEAGE: false,
                    CALCULATED_AMOUNT: ""
                });

                setAttachments([]);
                setInputError({});

                toast.success("Expenses Added.");
            })

			.catch((err) => {
					toast.error("Expenses Failed add.");
					common.c_LogWebError(PAGE_NAME,functionName,err);
			})

			.finally(() => {
					setLoading(false);
			});
	};

    const handleAdd = () => {

        let flag = true;

        if (!editData.DATE) {
            flag = false;
            setInputError((prev) => ({
                ...prev,
                DATE: "Date could not be empty."
            }));
        }

        if (!editData.CATEGORY_TYPE_MST_ID) {
            flag = false;
            setInputError((prev) => ({
                ...prev,
                CATEGORY_TYPE_MST_ID: "Type could not be empty."
            }));
        }

        if (!editData.AMOUNT || isNaN(editData.AMOUNT)) {
            flag = false;
            setInputError(prev => ({
                ...prev,
                AMOUNT: "Please enter a valid number."
            }));
        }

        if (!editData.EXPENSES_DESC) {
            flag = false;
            setInputError((prev) => ({
                ...prev,
                EXPENSES_DESC: "Description could not be empty."
            }));
        }

        // MEDICAL VALIDATION
        if (editData.IS_MEDICAL) {
            const entered = editData.IS_MILEAGE
                ? parseFloat(editData.CALCULATED_AMOUNT || 0)
                : parseFloat(editData.AMOUNT || 0);

            if (entered > medicalInfo.balance) {
                toast.error("Exceed medical claim balance!");
                return;
            }
        }

        if (flag) {

            if (editData.REQUIRED_RECEIPT === "YES" && attachments.length === 0) {
                toast.error("Attachment is required for this type.");
                return;
            }
            AddBtnOnClick();
        }
    };

    //ATTACHMENT 
    // const uploadAllFiles = async () => {
    //     for (let f of attachments) {
    //         try {
    //             const formData = new FormData();
    //             formData.append("file", f.file);
    //             formData.append("EXPENSES_DETAILS_ID", detailsId);
    //             formData.append("USER_ID", userId);

    //             await http.post("api/expenses/UploadExpensesAttachment", formData, {
    //                 headers: { "Content-Type": "multipart/form-data" }
    //             });

    //         } catch (err) {
    //             console.log("Upload failed for:", f.name, err);
    //             toast.error("Upload failed: " + f.name);
    //         }
    //     }
    // };

    // SUBMIT
    const ApplyExpenses = () => {

        let functionName = "ApplyExpenses";

        setLoading(true);
        setLoadingText("Applying Expenses...");

        const data = {
            EXPENSES_MST_ID: expensesId,
            USER_MST_GUID: userId,
            TOTAL_CLAIM: totalAmount,
            UPDATE_ID: userId,
            UPDATE_NAME: userName,
            DETAILS: allData,
            FROM_SOURCE: {
                SOURCE: "WEB",
                MODULE_ID
            }
        };

        http.post("api/expenses/ApplyExpenses", data)

            .then(() => {

								toast.success("Expenses submitted successfully.");

								setExpensesId("");
								setAllData([]);

								setTimeout(() => {
									navigate("/ExpensesHistory");
								}, 800);
						})

            .catch((err) => {

								toast.error(
										err?.response?.data?.MESSAGE ||
										"Failed to apply expenses."
								);

								common.c_LogWebError(PAGE_NAME,functionName,err);
						})

            .finally(() => {
                setLoading(false);
                setLoadingText("Loading...");
            });
    };

    const loadAttachments = async (detailsId) => {
        if (!detailsId) return;

        try {
            const res = await http.get(
                "api/expenses/GetExpensesAttachment?source=" + detailsId
            );

            const count = res.data?.length || 0;

            setAttachmentMap(prev => ({
                ...prev,
                [detailsId]: count
            }));

        } catch {
            setAttachmentMap(prev => ({
                ...prev,
                [detailsId]: 0
            }));
        }
    };

    const handleConfirm = () => {

        if (allData.length === 0) {
            toast.error("Table cannot be empty.");
            return;
        }

        const invalidRow = allData.find(
            row =>
                row.REQUIRED_RECEIPT === "YES" &&
                (!attachmentMap[row.EXPENSES_DETAILS_ID] || attachmentMap[row.EXPENSES_DETAILS_ID] === 0)
        );

        if (invalidRow) {
            toast.error("All required receipt items must have attachment.");
            return;
        }

        setShowConfirmModal(true);
    };

    const handleConfirmModal = async () => {
        try {
            // await uploadAllFiles();
            ApplyExpenses();
        } catch (err) {
            toast.error("Upload failed.");
        }
        setShowConfirmModal(false);
    };

    const handleConfirmModalClose = () => {
        setShowConfirmModal(false);
    };

		// UPDATE or DELETE
    const showUpdateModal = (e, row) => {
        setShowUpdate(true);
        const matchedType = categoryTypeList.find(t => t.value === row.CATEGORY_TYPE_MST_ID);
        setUpdateData({
            ...row,
            MILEAGE_RATE: matchedType?.mileageRate || row.MILEAGE_RATE,
            IS_MILEAGE: !!(row.IS_MILEAGE || row.CATEGORY_TYPE_NAME?.toLowerCase().includes("mileage")),
            IS_MEDICAL: !!(row.IS_MEDICAL || row.CATEGORY_TYPE_NAME?.toLowerCase().includes("medical"))
        });
    };

    const hideUpdateModal = () => {
        setShowUpdate(false);
        setUpdateData(null);
    };

    const showDeleteModal = (row) => {
        setShowDelete(true);
        setUpdateData(row);
    };

    const hideDeleteModal = () => {
        setShowDelete(false);
        setUpdateData(null);
    };

    const onReloadHandler = (deletedRow) => {
    if (!deletedRow) return;

    setAllData((prev) => {
        const updated = prev.filter(
            (x) => x.EXPENSES_DETAILS_ID !== deletedRow.EXPENSES_DETAILS_ID
        );

        if (updated.length === 0) {
            setExpensesId("");          // reset MST
            setAttachmentMap({});       // reset attachment map (optional but safer)
        }

        return updated;
    });
};

    const onLoadingHandler = (load, text) => {
        setLoading(load);
        setLoadingText(text);
    };

    const handleUpdateRow = (updatedRow) => {

        setAllData((prev) =>
            prev.map((row) =>
                row.EXPENSES_DETAILS_ID === updatedRow.EXPENSES_DETAILS_ID
                    ? updatedRow
                    : row
            )
        );
    };

    // HEADER
    const renderHeader = () => {
        return (
            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>

                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                        className="text-lg font-semibold"
                        style={{ fontSize: "18px" }}
                    >
                        Total Claimed
                    </span>

                    <span
                        className="text-lg font-semibold"
                        style={{
                            fontSize: "18px",
                            fontWeight: "bold"
                        }}
                    >
                        RM {totalAmount === 0 ? "0" : totalAmount.toFixed(2)}
                    </span>
                </div>

                <div
                    style={{
                        width: "2px",
                        backgroundColor: "#ccc",
                        height: "3.2rem",
                        margin: "0 15px"
                    }}
                ></div>

                <Tag
                    icon="pi pi-exclamation-triangle"
                    severity="warning"
                    value="Pending"
                    style={{
                        fontSize: "14px",
                        padding: "0 0.6rem",
                        height: "40px",
                        display: "flex",
                        alignItems: "center"
                    }}
                />

            </div>
        );
    };

    // Attachment
    const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp", "image/avif", "image/tiff", "image/heic", "image/heif", "image/svg+xml"];

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = [];

        for (let f of files) {
            if (!ALLOWED_TYPES.includes(f.type)) {
                toast.error(`"${f.name}" is not allowed. Only PDF and image files are accepted.`);
                continue;
            }
            if (f.size > 4 * 1024 * 1024) {
                toast.error("Max file size is 4MB");
                continue;
            }
            validFiles.push({ file: f, name: f.name, size: f.size });
        }

        setAttachments(prev => [...prev, ...validFiles]);
    };

        const removeFile = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <>
            {/* TOP FORM */}
            <div className={`${CssClass.cardTwo} ${CssClass.tableCardTwo} allowanceTableWrapper`}>

                <table className="allowanceTable">
                    <tr>

                        <td colSpan="3" style={{ paddingRight: "1rem" }}>
                            <Comp.Input
                                id="DATE"
                                label="Date*"
                                type="date"
                                value={editData.DATE}
                                onChange={inputHandler}
                                errorMessage={inputError.DATE}
                            />
                        </td>

                        <td colSpan="3" style={{ paddingRight: "1rem" }}>
                            <Comp.Select
                                id="CATEGORY_TYPE_MST_ID"
                                label="Type*"
                                options={categoryTypeList}
                                value={
                                    categoryTypeList.find(
                                        x => x.value === editData.CATEGORY_TYPE_MST_ID
                                    ) || ""
                                }
                                onChange={selectTypeHandler}
                                errorMessage={inputError.CATEGORY_TYPE_MST_ID}
                            />
                            {/* MEDICAL INFO */}
                            {editData.IS_MEDICAL && (
                                <div style={{
                                    marginTop: "5px",
                                    fontSize: "13px",
                                    color: "#444"
                                }}>
                                    Entitled: RM {medicalInfo.entitled.toFixed(2)} | 
                                    Claimed: RM {medicalInfo.claimed.toFixed(2)} | 
                                    Balance: <b>RM {medicalInfo.balance.toFixed(2)}</b>
                                </div>
                            )}

                            {/* MILEAGE INFO */}
                            {editData.IS_MILEAGE && (
                                <div style={{
                                    marginTop: "5px",
                                    fontSize: "13px",
                                    color: "#444"
                                }}>
                                    Rate: <b>RM {parseFloat(editData.MILEAGE_RATE || 0.5).toFixed(2)} per KM</b>
                                </div>
                            )}
                        </td>

                        <td colSpan="3" style={{ paddingRight: "1rem" }}>
                            <Comp.Input
                                id="AMOUNT"
                                // label="Amount (RM)*"
                                label={
                                    editData.IS_MILEAGE
                                        ? "Distance (KM)*"
                                        : "Amount (RM)*"
                                }
                                value={editData.AMOUNT}
                                onChange={(e) => {
                                    const val = e.target.value;

                                    // allow only number
                                    if (!/^\d*\.?\d*$/.test(val)) return;

                                    if (editData.IS_MILEAGE) {
                                        const km = parseFloat(val || 0);
                                        const rate = parseFloat(editData.MILEAGE_RATE || 0.5);
                                        const rm = km * rate;

                                        setEditData(prev => ({
                                            ...prev,
                                            AMOUNT: val,
                                            CALCULATED_AMOUNT: rm.toFixed(2)
                                        }));
                                    } else {
                                        setEditData(prev => ({
                                            ...prev,
                                            AMOUNT: val
                                        }));
                                    }
                                }}
                            />
                        </td>

                        <td colSpan="3">
                            <label style={{ padding: "6px" }}>
																Required Receipt
														</label>

														<input
																type="text"
																className="form-control-allowance"
																value={editData.REQUIRED_RECEIPT}
																readOnly
																style={{
																		backgroundColor: "#f2f2f2",
																		color: "#666"
																}}
														/>
                        </td>

                    </tr>

                    <tr>

                        <td colSpan="6" style={{ paddingRight: "1rem" }}>
                            <label style={{ padding: "6px" }}>
                                Description*
                            </label>

                            <textarea
                                id="EXPENSES_DESC"
                                rows={2}
                                className="form-control-allowance"
                                value={editData.EXPENSES_DESC}
                                onChange={inputHandler}
                            />

                            {inputError.EXPENSES_DESC && (
                                <div className="text-danger mt-1">
                                    {inputError.EXPENSES_DESC}
                                </div>
                            )}
                        </td>

                        <td colSpan="6">
                            <label style={{ padding: "6px" }}>
                                Remark
                            </label>

                            <textarea
																id="REMARK"
																rows={2}
																className="form-control-allowance"
																value={editData.REMARK}
																readOnly
																style={{
																		backgroundColor: "#f2f2f2",
																		color: "#666"
																}}
														/>
                        </td>

                    </tr>

                    {/* Attachment */}
                    <tr>
                        <td colSpan="12" style={{ padding: "2rem 0" }}>
                            <div
                            style={{
                                display: "flex",
                                border: "1px solid #ddd",
                                borderRadius: "6px",
                                overflow: "hidden",
                                // width: "49%",
                                width: "100%",
                                minWidth: "500px" 
                            }}
                            >
                            {/* LEFT GREY LABEL (INSIDE BOX) */}
                            <div
                                style={{
                                width: "220px",
                                background: "#f5f5f5",
                                padding: "10px",
                                borderRight: "1px solid #ddd",
                                fontWeight: "500"
                                }}
                            >
                                Upload File (PDF and Image file only)
                                <div style={{ fontSize: "12px", color: "#555", marginTop: "4px" }}>
                                (Max File Size 4MB per file)
                                </div>
                            </div>

                            {/* RIGHT TABLE */}
                            <div style={{ flex: 1 }}>
                                <table style={{ width: "100%", fontSize: "1rem", borderCollapse: "collapse" }}>
                                <thead style={{ background: "#f5f5f5" }}>
                                    <tr>
                                    <th style={{ width: "40px", textAlign: "center", padding: "0 8px", height: "38px", verticalAlign: "middle", border: "1px solid #ddd" }}>#</th>
                                    <th style={{ textAlign: "left", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>Filename</th>
                                    <th style={{ width: "100px", textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>Size</th>
                                    <th style={{ width: "80px", textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>Action</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {attachments.map((f, i) => (
                                        <tr key={i} style={{ height: "38px" }}>
                                            <td style={{ textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>{i + 1}</td>
                                            <td style={{ padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>{f.name}</td>
                                            <td style={{ textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>
                                                {(f.size / 1024).toFixed(0)} KB
                                            </td>
                                            <td style={{ textAlign: "center", padding: "0 8px", verticalAlign: "middle", border: "1px solid #ddd" }}>
                                                <span
                                                    style={{ color: "#d9534f", cursor: "pointer" }}
                                                    onClick={() => removeFile(i)}
                                                >
                                                    DELETE
                                                </span>
                                            </td>
                                        </tr>
                                    ))}

                                    {/* ALWAYS SHOW ADD BUTTON */}
                                    <tr style={{ height: "38px" }}>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                            <label style={{ cursor: "pointer", fontWeight: "bold", fontSize: "20px" }}>
                                                +
                                                <input
                                                    type="file"
                                                    multiple
                                                    accept=".pdf,.jpg,.jpeg,.png,.webp,.avif,.tif,.tiff,.heic,.heif,.svg"
                                                    style={{ display: "none" }}
                                                    onChange={(e) => {
                                                        handleFileSelect(e);
                                                        e.target.value = null;
                                                    }}
                                                />
                                            </label>
                                        </td>
                                    </tr>
                                </tbody>
                                </table>
                            </div>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td
                            colSpan="12"
                            style={{ padding: "0.5rem 1rem 0.5rem 0" }}
                        >
                            <div style={{ display: "flex", gap: "1rem" }}>

                                <Comp.Button
                                    id="btnAdd"
                                    type="submit"
                                    onClick={handleAdd}
                                >
                                    <FontAwesomeIcon icon={faSquarePlus} /> ADD
                                </Comp.Button>

                                <Comp.Button
                                    id="btnSubmit"
                                    type="submit"
                                    onClick={handleConfirm}
                                >
                                    Submit <FontAwesomeIcon icon={faFileImport} />
                                </Comp.Button>

                            </div>
                        </td>
                    </tr>

                </table>

            </div>

            {/* TABLE */}
            <div className={`${CssClass.cardTwo} ${CssClass.tableCardTwo}`}>

                <div style={{ width: "100%" }}>

                    <DataTable
                        key={Object.keys(attachmentMap).join(",")}
                        value={allData}
                        dataKey="EXPENSES_DETAILS_ID"
                        header={renderHeader()}
                        loading={loading}
                        removableSort
                        emptyMessage="Fill in and add to expenses claim."
                        scrollable
                        scrollHeight="700px"
                        showGridlines
                        className="datatable"
                        tableStyle={{ minWidth: "90rem" }}
                    >

                        <Column
                            header="Action"
                            body={(rowData) => (
                                <div
                                    style={{ textAlign: "-webkit-center" }}
                                    className={CssClass.tdBtnActionWrapperTwo}
                                >
                                    <span title="Modify">
                                        <Comp.Button
                                            id="btnEdit"
                                            type="general"
                                            onClick={(e) =>
                                                showUpdateModal(e, rowData)
                                            }
                                            style={{
                                                width: "50px",
                                                height: "35px",
                                                marginTop: "2px",
                                                marginBottom: "2px"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faPenToSquare} />
                                        </Comp.Button>
                                    </span>

                                    <span title="Delete">
                                        <Comp.Button
                                            id="btnDelete"
                                            type="delete"
                                            onClick={() =>
                                                showDeleteModal(rowData)
                                            }
                                            style={{
                                                width: "50px",
                                                height: "35px"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </Comp.Button>
                                    </span>
                                </div>
                            )}
                            style={{ width: "90px" }}
                        />

                        <Column
                            header="#"
                            body={(rowData, options) => options.rowIndex + 1}
                            style={{
                                width: "2.5rem",
                                textAlign: "center"
                            }}
                        />

                        <Column
                            field="DATE"
                            header="Date"
                            style={{ textAlign: "center" }}
                            body={(rowData) => {
                                if (!rowData.DATE) return "";

                                const d = new Date(rowData.DATE);

                                if (isNaN(d)) return "";

                                const day = String(d.getDate()).padStart(2, "0");
                                const month = String(d.getMonth() + 1).padStart(2, "0");
                                const year = d.getFullYear();

                                return `${day}/${month}/${year}`;
                            }}
                        />

                        <Column
                            field="CATEGORY_TYPE_NAME"
                            header="Type"
                            style={{ textAlign: "center" }}
                        />

                        <Column
                            field="EXPENSES_DESC"
                            header="Description"
                            style={{ minWidth: "250px" }}
                        />

                        <Column
                            field="AMOUNT"
                            header="Amount (RM)"
                            style={{ textAlign: "right" }}
                            bodyStyle={{
                                backgroundColor: "#ebf5eb",
                                fontWeight: "bold"
                            }}
                            body={(row) => {
                                const amount = parseFloat(row.AMOUNT || 0);

                                return (
                                    <div style={{ textAlign: "right" }}>
                                        RM {amount.toFixed(2)}
                                    </div>
                                );
                            }}
                        />

                        <Column
                            header="Attachment"
                            style={{ textAlign: "center", width: "120px" }}
                            headerStyle={{ textAlign: "center" }}
                            body={(row) => {
                                const count = attachmentMap[row.EXPENSES_DETAILS_ID];

                                // console.log("Row ID:", row.EXPENSES_DETAILS_ID, "Count:", count);
                                // console.log("attachmentMap:", attachmentMap);
                                // console.log("allData:", allData);

                                if (count === undefined) return "-";   // not loaded yet
                                if (count === 0) return "-";           // no attachment

                                return (
                                    <i
                                        className="pi pi-file"
                                        style={{ cursor: "pointer", fontSize: "16px" }}
                                        onClick={() => openAttachmentModal(row.EXPENSES_DETAILS_ID)}
                                    />
                                );
                            }}
                        />

                    </DataTable>

                </div>

            </div>

            {/* UPDATE */}
            <ExpensesDetailsUpdate
                page={props.page}
                module={MODULE_ID}
                show={showUpdate}
                editData={updateData}
                onHide={hideUpdateModal}
                onReload={onReloadHandler}
                onLoading={onLoadingHandler}
                onUpdateRow={handleUpdateRow}
                medicalInfo={medicalInfo}
            />

            {/* CONFIRM */}
            <ConfirmModal
                show={showConfirmModal}
                onHide={handleConfirmModalClose}
                onConfirm={handleConfirmModal}
                prop="Confirm to apply"
            >
                Are you sure you want to apply this expenses?
            </ConfirmModal>

            {/* DELETE */}
            {showDelete && (
                <ExpensesDetailsDelete
                    page={PAGE_NAME}
                    module={MODULE_ID}
                    showHide={showDelete}
                    editData={updateData}
                    onHide={hideDeleteModal}
                    onReload={onReloadHandler}
                    onLoading={onLoadingHandler}
                />
            )}

            {/* Attachment Modal */}
            <ExpensesAttachmentModal
                show={showAttachmentModal}
                onHide={() => setShowAttachmentModal(false)}
                source={attachmentSource}
                userId={userId}
            />

            {loading && <Comp.Loading>{loadingText}</Comp.Loading>}
            <Comp.AlertPopup />
        </>
    );
};

export default Expenses;